import { environmentHttpClient as httpClient } from "client/Api/httpClient";
import {
	HubCommentModel,
	HubCommentPagingInformationModel,
	HubCommentSaveModel,
	HubCommentStatisticsModel,
	HubLinkedDocumentViewModel,
	ReactionCounterModel,
	ReactionKind,
	ReactionUserModel
} from "../Hub/common";
import {
	UserIdentityModel
} from "../UserProfile/service";
import { CommunityPostOptionsModel, CommunityViewModel } from "./communityPosts";
import { SortDirection } from "../common";

export type CommunityPostDetailsPageViewModel = {
	post: CommunityPostDetailsViewModel;
	options: CommunityPostOptionsModel;
};

export type CommunityPostImageViewModel = {
	id: number;
	blobId: string;
	name: string;
};

export type CommunityPostStatisticsModel = {
	currentUserReaction: ReactionKind;
	reactionCounters: readonly ReactionCounterModel[];
	commentsCount: number;
};

export type CommunityPostDetailsViewModel = {
	id: number;
	message: string | null;
	images: readonly CommunityPostImageViewModel[];
	createdOn: Date | string;
	modifiedOn: Date | string;
	createdBy: UserIdentityModel | null;
	community: CommunityViewModel;
	isEditable: boolean;
	isFollowing: boolean;
	statistics: CommunityPostStatisticsModel;
	hasPoll: boolean;
	linkedDocuments: readonly HubLinkedDocumentViewModel[];
};

export function getCommunityPostDetails(
	postId: number,
	signal?: AbortSignal
): Promise<CommunityPostDetailsPageViewModel> {
	return httpClient.get<CommunityPostDetailsPageViewModel>(
		`UiApi/Communities/Posts/${postId}/Details`,
		undefined,
		{ signal }
	);
}

export function getStatistics(
	postId: number,
	signal?: AbortSignal
): Promise<CommunityPostStatisticsModel> {
	return httpClient.get<CommunityPostStatisticsModel>(
		`UiApi/Communities/Posts/${postId}/Statistics`,
		undefined,
		{ signal }
	);
}

export function getReactions(
	postId: number,
	pageSize: number,
	skip: number,
	kind: ReactionKind | undefined,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<ReactionUserModel>> {
	return httpClient.get<LazyLoadPageResult<ReactionUserModel>>(
		`UiApi/Communities/Posts/${postId}/Reactions`,
		{
			kind,
			pageSize,
			skip
		},
		{ signal }
	);
}

export function registerReaction(
	id: number,
	kind: ReactionKind,
	signal?: AbortSignal
): Promise<CommunityPostStatisticsModel> {
	return httpClient.put<CommunityPostStatisticsModel>(
		`UiApi/Communities/Posts/${id}/Reaction`,
		kind,
		{ signal }
	);
}

export function getCommentsList(
	postId: number,
	amount: number | null,
	afterComment: HubCommentPagingInformationModel | null,
	sortDirection: SortDirection,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<HubCommentModel>> {
	return httpClient.get<LazyLoadPageResult<HubCommentModel>>(
		`/UiApi/Communities/Posts/${postId}/Comments`,
		{
			amount,
			afterComment,
			sortDirection
		},
		{ signal }
	);
}

export function getRelatedCommentThread(
	postId: number,
	commentId: number,
	signal?: AbortSignal
): Promise<readonly HubCommentModel[]> {
	return httpClient.get<readonly HubCommentModel[]>(
		`/UiApi/Communities/Posts/${postId}/Comments/${commentId}/Thread`,
		undefined,
		{ signal }
	);
}

export function registerCommentReaction(
	postId: number,
	commentId: number,
	kind: ReactionKind,
	signal?: AbortSignal
): Promise<HubCommentStatisticsModel> {
	return httpClient.put<HubCommentStatisticsModel>(
		`/UiApi/Communities/Posts/${postId}/Comments/${commentId}/Reaction`,
		kind,
		{ signal }
	);
}

export function getCommentReactionCounts(
	postId: number,
	commentId: number,
	signal?: AbortSignal
): Promise<readonly ReactionCounterModel[]> {
	return httpClient.get<readonly ReactionCounterModel[]>(
		`/UiApi/Communities/Posts/${postId}/Comments/${commentId}/Reactions/Counts`,
		undefined,
		{ signal }
	);
}

export function getCommentReactions(
	postId: number,
	commentId: number,
	pageSize: number,
	skip: number,
	kind: ReactionKind | undefined,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<ReactionUserModel>> {
	return httpClient.get<LazyLoadPageResult<ReactionUserModel>>(
		`/UiApi/Communities/Posts/${postId}/Comments/${commentId}/Reactions`,
		{
			kind,
			pageSize,
			skip
		},
		{ signal }
	);
}

export function doesUserHaveAccess(
	postId: number,
	userId: number,
	signal?: AbortSignal
): Promise<boolean> {
	return httpClient.get<boolean>(
		`UiApi/Communities/Posts/${postId}/DoesUserHaveAccess`,
		{
			userId
		},
		{ signal }
	);
}

export function findUsers(
	postId: number,
	searchTerm: string,
	amount: number,
	signal?: AbortSignal
): Promise<readonly UserIdentityModel[]> {
	return httpClient.get<readonly UserIdentityModel[]>(
		`UiApi/Communities/Posts/${postId}/Users`,
		{
			searchTerm,
			amount
		},
		{ signal }
	);
}

export function createComment(
	postId: number,
	newComment: HubCommentSaveModel,
	signal?: AbortSignal
): Promise<HubCommentModel> {
	return httpClient.post<HubCommentModel>(
		`/UiApi/Communities/Posts/${postId}/Comments`,
		newComment,
		{ signal }
	);
}

export function editComment(
	postId: number,
	commentId: number,
	updatedComment: HubCommentSaveModel,
	signal?: AbortSignal
): Promise<HubCommentModel> {
	return httpClient.put<HubCommentModel>(
		`/UiApi/Communities/Posts/${postId}/Comments/${commentId}`,
		updatedComment,
		{ signal }
	);
}

export function deleteComment(
	postId: number,
	commentId: number,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.delete<void>(
		`/UiApi/Communities/Posts/${postId}/Comments/${commentId}`,
		undefined,
		{ signal }
	);
}

export function createReply(
	postId: number,
	parentCommentId: number,
	newComment: HubCommentSaveModel,
	signal?: AbortSignal
): Promise<HubCommentModel> {
	return httpClient.post<HubCommentModel>(
		`/UiApi/Communities/Posts/${postId}/Comments/${parentCommentId}/Replies`,
		newComment,
		{ signal }
	);
}

export function followCommunityPost(id: number, signal?: AbortSignal): Promise<void> {
	return httpClient.post<void>(
		`UiApi/Communities/Posts/${id}/Subscription`,
		{ signal }
	);
}

export function unFollowCommunityPost(id: number, signal?: AbortSignal): Promise<void> {
	return httpClient.delete<void>(
		`UiApi/Communities/Posts/${id}/Subscription`,
		{ signal }
	);
}

export type CommunityPostPollAnswerViewModel = {
	id: number;
	text: string;
};

export type CommunityPostPollStatistics = {
	didCurrentUserVote: boolean;
	voteCounts: Record<string, number>;
};

export type CommunityPostPollViewModel = {
	question: string;
	options: readonly CommunityPostPollAnswerViewModel[];
	isMultiSelect: boolean;
	statistics: CommunityPostPollStatistics;
};

export function getPoll(
	postId: number,
	signal: AbortSignal
): Promise<CommunityPostPollViewModel> {
	return httpClient.get<CommunityPostPollViewModel>(
		`/UiApi/Communities/Posts/${postId}/Poll`,
		undefined,
		{ signal }
	);
}

export type CommunityPostPollVoteModel = {
	optionIds: readonly number[];
};

export function vote(
	postId: number,
	model: CommunityPostPollVoteModel,
	signal: AbortSignal
): Promise<CommunityPostPollStatistics> {
	return httpClient.post<CommunityPostPollStatistics>(
		`/UiApi/Communities/Posts/${postId}/Poll/Vote`,
		model,
		{ signal }
	);
}

export function resetVote(
	postId: number,
	signal: AbortSignal
): Promise<CommunityPostPollStatistics> {
	return httpClient.delete<CommunityPostPollStatistics>(
		`/UiApi/Communities/Posts/${postId}/Poll/Vote`,
		undefined,
		{ signal }
	);
}

type ListCommunityPollVotersModel = {
	skip: number;
	pageSize: number;
};

export function listVoters(
	postId: number,
	optionId: number,
	request: ListCommunityPollVotersModel,
	signal: AbortSignal
): Promise<LazyLoadPageResult<UserIdentityModel>> {
	return httpClient.get<LazyLoadPageResult<UserIdentityModel>>(
		`/UiApi/Communities/Posts/${postId}/Poll/Options/${optionId}/Voters`,
		request,
		{ signal }
	);
}